"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Mail, MapPin, Phone, User2 } from "lucide-react"
import { Textarea } from "../ui/textarea"
import { contactUsFromSchema, type ContactUsFromSchema } from "@/lib/validation"
import { useTransition } from "react"
import { contactUsFromAction } from "@/server/actions/google-sheets-actions"
import { toast } from "sonner"
import LoadingButton from "../ui/loading-button"

const ContactForm = () => {
    const [isPending, startTransition] = useTransition()

    const form = useForm<ContactUsFromSchema>({
        resolver: zodResolver(contactUsFromSchema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            destination: "",
            message: "",
        },
    })

    function onSubmit(data: ContactUsFromSchema) {
        startTransition(async () => {
            const res = await contactUsFromAction(data);
            if (!res.success) {
                toast.error(res.message)
                return;
            }

            toast.success(res.message)
            form.reset()
        })
    }

    const months = [
        "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
    ];

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-4">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Your Name</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Your Name" {...field} className="h-12 peer" />
                                        <User2 className="h-5 w-5 absolute top-1/2 right-2 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Your Email</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Your Email" {...field} className="h-12 peer" />
                                        <Mail className="h-5 w-5 absolute top-1/2 right-2 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="grid lg:grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Your Phone Number</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input placeholder="Enter Phone Number" {...field} className="h-12 peer" />
                                            <Phone className="h-5 w-5 absolute top-1/2 right-2 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="month"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Select Month</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="h-12 border-zinc-400 focus-visible:ring-custom-primary">
                                                <SelectValue placeholder="Select any month of your choice" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent className="max-h-44 focus-visible:ring-custom-primary">
                                            {months.map((month) => (
                                                <SelectItem key={month} value={month}>{month}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="destination"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Destination</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Your Destination" {...field} className="h-12 peer" />
                                        <MapPin className="h-5 w-5 absolute top-1/2 right-2 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Your Message</FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Enter Phone Number"
                                        rows={4}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <LoadingButton loading={isPending} type="submit" className="primary-btn bg-custom-primary w-full text-base">
                        Submit Now
                    </LoadingButton>
                </form>
            </Form>
        </div>
    )
}

export default ContactForm